import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { cn, renderDisplayText } from "@/lib/utils";
import { LoadingLabel, Skel } from "@/components/home/TenantSkeletons";
import { GoogleGlyph } from "@/components/reviews/GoogleGlyph";
import type { HeroModel, ReviewsModel, ServicesModel, TrustItem } from "@/components/home/layouts/models";
import {
  Avatar, HeroActions, HeroCopySkeleton, HeroTitle, Photo, ReviewExtras, ReviewerLine, StarRow,
  type HeroViewProps,
} from "@/components/home/layouts/shared";

/** Bold & Geometric: full-bleed photo, giant uppercase headline, angled edge. */
export function BoldHero({ m, edit }: HeroViewProps) {
  const seq = (n: number) => ({ ["--seq" as string]: n });
  return (
    <section className="v-bold-hero relative isolate overflow-hidden" aria-busy={!m.ready}>
      <div className="absolute inset-0 -z-20 bg-neutral-900">
        {m.heroPending ? null : edit?.image ?? <Photo src={m.heroImage} alt="Professional cleaner at work" fetchPriority="high" decoding="async" />}
      </div>
      <div className="pointer-events-none absolute inset-0 -z-10" style={{ background: "linear-gradient(100deg, hsl(222 47% 6% / 0.92) 0%, hsl(222 47% 6% / 0.72) 45%, hsl(222 47% 6% / 0.25) 100%)" }} aria-hidden />
      <div className="v-bold-bar pointer-events-none absolute inset-y-0 left-0 -z-10 w-2 bg-primary md:w-3" aria-hidden />
      <div className="container relative mx-auto px-5 pb-32 pt-16 md:px-6 md:pb-40 md:pt-24">
        <div className="max-w-4xl">
          {m.ready ? (
            <>
              <p className="tp-eyebrow tp-seq !text-white/85" style={seq(0)}>{m.eyebrow}</p>
              <HeroTitle m={m} edit={edit} className="tp-display v-bold-title tp-seq mt-6 !text-white md:mt-8" />
              <div className="tp-lead tp-seq mt-7 max-w-xl !text-white/80" style={seq(2)}>{edit?.subtitle ?? <p>{m.subtitle}</p>}</div>
              <div className="tp-seq mt-10" style={seq(3)}><HeroActions m={m} edit={edit} ghostClassName="!border-white/50 !bg-transparent !text-white hover:!bg-white/10" /></div>
            </>
          ) : (
            <HeroCopySkeleton className="opacity-60" />
          )}
        </div>
      </div>
    </section>
  );
}

/** Colour-block stats strip that overlaps the angled hero edge. */
export function BoldStats({ m, items }: { m: HeroModel; items: TrustItem[] }) {
  const cells = [
    { key: "rating", big: m.reviewsPending ? "–" : m.rating.toFixed(1), label: m.reviewCount > 0 ? `Average from ${m.reviewCount} review${m.reviewCount === 1 ? "" : "s"}` : m.ratingLabel, stars: true },
    ...items.slice(0, 3).map((it) => ({ key: it.key, big: it.title, label: it.subtitle, stars: false })),
  ];
  return (
    <section aria-label="Why customers trust us" className="relative z-10 -mt-20 md:-mt-28">
      <div className="container mx-auto px-5 md:px-6">
        <ul className="grid grid-cols-2 lg:grid-cols-4">
          {cells.map((c, i) => (
            <li key={c.key} data-reveal style={{ ["--reveal-delay" as string]: `${i * 70}ms` }}
              className={cn("v-bold-cell flex flex-col justify-between gap-6 p-5 md:p-8", i % 2 === 0 ? "bg-primary text-primary-foreground" : "bg-neutral-900 text-white", i === 1 && "lg:bg-neutral-900", i === 2 && "lg:bg-primary", i === 3 && "bg-neutral-900")}>
              <span className={cn("font-display font-black uppercase leading-[0.95] tracking-tight", c.stars ? "tp-numeral !font-black text-5xl md:text-7xl" : "text-xl md:text-3xl")}>{c.big}</span>
              <span>
                {c.stars && <StarRow className="mb-2 block text-base !text-current" />}
                <span className="block text-xs font-semibold uppercase tracking-[0.14em] opacity-80 md:text-sm">{c.label}</span>
              </span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export function BoldServices({ s }: { s: ServicesModel }) {
  return (
    <section className="tp-section">
      <div className="container mx-auto px-5 md:px-6">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="tp-eyebrow" data-reveal>What we do</p>
            <h2 className="tp-h2 mt-4" data-reveal>{renderDisplayText(s.title)}</h2>
          </div>
          <Link to={s.servicesHref} className="v-bold-link text-sm" data-reveal>All services <ArrowRight className="h-4 w-4" /></Link>
        </div>
        {s.loading ? (
          <div className="tp-skeleton mt-12 space-y-8" aria-busy="true"><LoadingLabel>Loading services</LoadingLabel>{[0, 1].map((i) => <Skel key={i} className="h-72 w-full rounded-none" />)}</div>
        ) : (
          <div className="mt-12 space-y-10 md:mt-16 md:space-y-0">
            {s.items.map((item, i) => (
              <article key={item.key} className="group relative grid items-stretch md:grid-cols-2" data-reveal>
                <div className={cn("v-bold-media relative aspect-[16/11] overflow-hidden bg-muted md:aspect-auto md:min-h-[24rem]", i % 2 === 1 && "md:order-2")}>
                  <Photo src={item.image} alt={item.category} className="tp-zoom" loading="lazy" />
                  <span className="absolute left-0 top-0 bg-primary px-4 py-2 font-display text-2xl font-black text-primary-foreground md:text-4xl">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <div className={cn("relative flex flex-col justify-center gap-4 p-7 md:p-14", i % 2 === 0 ? "bg-secondary" : "bg-neutral-900 text-white")}>
                  <span className="absolute right-6 top-6 h-10 w-10 border-4 border-primary" aria-hidden />
                  <h3 className={cn("tp-h3 !text-[clamp(1.6rem,1.2rem+1.6vw,2.6rem)]", i % 2 === 1 && "!text-white")}>{renderDisplayText(item.title)}</h3>
                  {item.description && <p className={cn("max-w-md leading-relaxed", i % 2 === 1 ? "text-white/75" : "text-muted-foreground")}>{item.description}</p>}
                  <p className="font-display text-lg font-black uppercase tracking-wide text-primary">{item.price}</p>
                  <Link to={s.servicesHref} className="v-bold-link mt-2 self-start text-sm">Book it <ArrowRight className="h-4 w-4" /></Link>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export function BoldReviews({ r, reviewFormHref }: { r: ReviewsModel; reviewFormHref?: string }) {
  return (
    <section className="tp-section v-bold-reviews relative bg-neutral-900 text-white" aria-busy={r.pending}>
      <div className="container mx-auto px-5 md:px-6">
        <div className="flex flex-wrap items-end justify-between gap-8">
          <div>
            <p className="tp-eyebrow !text-white/80">Reviews</p>
            <h2 className="tp-h2 mt-4 !text-white">What our customers say</h2>
          </div>
          {!r.pending && (
            <div className="flex items-center gap-5">
              <span className="tp-numeral !font-black text-7xl leading-none text-primary">{r.average.toFixed(1)}</span>
              <span><StarRow n={r.average} className="block text-lg" /><span className="mt-1 block text-xs font-semibold uppercase tracking-[0.14em] text-white/70">{r.countLine}</span></span>
            </div>
          )}
        </div>
        {r.pending ? (
          <div className="tp-skeleton mt-12 grid gap-6 md:grid-cols-3" aria-hidden><LoadingLabel>Loading reviews</LoadingLabel>{[0, 1, 2].map((i) => <Skel key={i} className="h-56 w-full rounded-none" />)}</div>
        ) : (
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {r.items.map((item) => (
              <figure key={item.key} className="flex flex-col border-t-4 border-primary bg-white/[0.06] p-7" data-reveal data-testid={item.google ? "google-review-card" : undefined}>
                <div className="flex items-center justify-between"><StarRow n={item.rating} className="text-base" />{item.google && <GoogleGlyph className="h-5 w-5" />}</div>
                <blockquote className="mt-5 flex-1"><p className="line-clamp-6 text-lg font-medium leading-relaxed text-white">{item.quote}</p></blockquote>
                <figcaption className="mt-7 flex items-center gap-3"><Avatar item={item} className="h-10 w-10 !rounded-none" /><div className="min-w-0 [&_*]:!text-white/80 [&_a]:!text-white"><ReviewerLine item={item} /></div></figcaption>
              </figure>
            ))}
          </div>
        )}
        {!r.pending && <ReviewExtras google={r.google} reviewFormHref={reviewFormHref} className="mt-10 [&_a:not(.tp-btn)]:!text-white" />}
      </div>
    </section>
  );
}
